import React, { useEffect, useState, useRef, useCallback } from "react";
import { Loader2, Upload, Trash2, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { adminGet, adminDelete, adminUploadMedia, formatApiError, resolveAssetUrl } from "../../lib/api";

const MediaLibrary = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminGet("/media");
      setItems(d);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onPick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        await adminUploadMedia(f);
      }
      toast.success(`Uploaded ${files.length} file${files.length > 1 ? "s" : ""}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this file? It cannot be undone.")) return;
    try {
      await adminDelete(`/media/${id}`);
      setItems((s) => s.filter((m) => m.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyUrl = async (url) => {
    const full = resolveAssetUrl(url);
    try {
      await navigator.clipboard.writeText(full);
      setCopied(url);
      setTimeout(() => setCopied(null), 1500);
      toast.success("URL copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <div data-testid="admin-media-page">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-display text-[11px] uppercase tracking-[0.3em] text-[#0A192F]/60">WordPress-style media</div>
          <h1 className="mt-2 font-display font-black text-4xl text-[#0A192F] tracking-tighter">
            Media Library ({items.length})
          </h1>
        </div>
        <button
          onClick={onPick}
          disabled={uploading}
          data-testid="admin-media-upload-btn"
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A192F] px-6 py-3 font-display text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#0A192F] hover:text-white transition-colors disabled:opacity-60"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? "Uploading…" : "Upload Files"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,video/mp4,video/webm"
          multiple
          onChange={onChange}
          className="hidden"
          data-testid="admin-media-file-input"
        />
      </header>

      <p className="text-sm text-[#0A192F]/60 mb-6">
        Upload images and videos here. Each file gets a permanent URL you can copy and paste into any editor
        (Hero, Gallery, About, etc).
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#0A192F]/40">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div
          onClick={onPick}
          className="border-2 border-dashed border-gray-300 p-16 text-center cursor-pointer hover:border-[#D4AF37] transition-colors"
        >
          <Upload size={32} className="mx-auto text-[#0A192F]/40" />
          <div className="mt-4 font-display font-bold text-[#0A192F]">No files yet</div>
          <div className="text-sm text-[#0A192F]/60 mt-1">Click here or the button above to upload images.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m) => {
            const url = resolveAssetUrl(m.public_url);
            const isImage = (m.content_type || "").startsWith("image/");
            return (
              <div
                key={m.id}
                data-testid={`admin-media-item-${m.id}`}
                className="bg-white border border-gray-200 overflow-hidden group hover:border-[#D4AF37] transition-colors"
              >
                <div className="aspect-square bg-gray-100">
                  {isImage ? (
                    <img src={url} alt={m.filename} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <video src={url} className="w-full h-full object-cover" muted />
                  )}
                </div>
                <div className="p-3">
                  <div className="text-xs font-semibold text-[#0A192F] truncate">{m.filename}</div>
                  <div className="text-[10px] text-[#0A192F]/50 mt-1">
                    {Math.round((m.size || 0) / 1024)} KB
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => copyUrl(m.public_url)}
                      data-testid={`admin-media-copy-${m.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1 text-[10px] font-display uppercase tracking-wider border border-gray-300 py-2 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                    >
                      {copied === m.public_url ? <Check size={12} /> : <Copy size={12} />}
                      {copied === m.public_url ? "Copied" : "Copy URL"}
                    </button>
                    <button
                      onClick={() => remove(m.id)}
                      data-testid={`admin-media-delete-${m.id}`}
                      className="inline-flex items-center justify-center w-9 border border-gray-300 hover:border-red-500 hover:text-red-500"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
